import Delete from '@mui/icons-material/Delete';
import { Avatar, Box, Grid, Select, Typography } from '@mui/material';
import { isEmpty, map, range } from 'lodash';
import React, { useState } from 'react';
import PhoneInput from '../../../components/PhoneInput';
import moment from 'moment';
import validation from '../../../input-validation.service';
import axios from 'axios';
import { useMutation } from 'react-query';
import { updateUserInfo } from '../../../utils/api';
import { refresh } from '../../../utils/index';
import IconButton from '../../../components/IconButton';
import Button from '../../../components/Button';
import TextField from '../../../components/TextInput';
import SelectInput from '../../../components/SelectInput';
import { ICON_BUTTON_TYPE } from '../../../constants';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUserLocation } from '../../../hooks/useUserLocation';

const PersonalInfo = ({ user }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { country } = useUserLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const imageRef = React.useRef();
  const [state, setState] = React.useState({
    firstName: '',
    lastName: '',
    imageUrl: '',
    email: '',
    username: '',
    gender: '',
    birthdate: '',
    weight: '',
    height: '',
    goal: 0,
    errorMessage: '',
    imageToDisplay: '',
  });

  React.useEffect(() => {
    if (!isEmpty(user)) {
      setState((prev) => {
        // Format weight to include 'lb' if not already present
        let formattedWeight = user?.userInfo?.weight || '';
        if (formattedWeight && !formattedWeight.includes('lb')) {
          formattedWeight = `${formattedWeight} lb`;
        }

        // Get the height value
        let formattedHeight = user?.userInfo?.height || '';
        // Check if the height value is in the correct format for the SelectInput
        const heightOptionsList = heightOptions();
        if (formattedHeight && !heightOptionsList.includes(formattedHeight)) {
          // If the height is not in the correct format, find closest match
          const heightInInches = parseInt(formattedHeight.split("'")[0]) * 12 + parseInt(formattedHeight.split("'")[1]);
          const closestHeight = heightOptionsList.reduce((prev, curr) => {
            const prevInches = parseInt(prev.split("'")[0]) * 12 + parseInt(prev.split("'")[1]);
            const currInches = parseInt(curr.split("'")[0]) * 12 + parseInt(curr.split("'")[1]);
            return Math.abs(currInches - heightInInches) < Math.abs(prevInches - heightInInches) ? curr : prev;
          });
          formattedHeight = closestHeight;
        }

        return {
          ...prev,
          firstName: user?.userInfo?.firstName || '',
          lastName: user?.userInfo?.lastName || '',
          weight: formattedWeight,
          height: formattedHeight,
          gender: user?.userInfo?.gender || '',
          imageUrl: !isEmpty(user?.userInfo?.imageUrl) ? user?.userInfo?.imageUrl.split('?')[0] : '',
          imageToDisplay: user?.userInfo?.imageUrl || '',
          email: user?.email || '',
          username: user?.username || '',
          goal: user?.goal?.daily?.at(-1)?.value || 0,
          birthdate: moment(user?.userInfo?.birthdate).format('YYYY-MM-DD') || '',
        };
      });
    }
  }, [user]);

  const updateUserInfoHandler = useMutation(updateUserInfo, {
    onSuccess: async ({ data }) => {
      await refresh();
    },
    onError: (error) => {
      console.log('error =====> ', error);
      if (error?.response?.status === 400) {
        return setState((prev) => {
          return { ...prev, errorMessage: error?.response?.data?.data?.join(',') || t('Something went wrong!') };
        });
      } else {
        return setState((prev) => {
          return { ...prev, errorMessage: error?.response?.data?.message || t('Something went wrong!') };
        });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const { firstName, lastName, imageUrl, username, birthdate, height, weight, gender, goal } = state;

    if (
      isEmpty(firstName) ||
      isEmpty(lastName) ||
      isEmpty(birthdate) ||
      isEmpty(username) ||
      isEmpty(height) ||
      isEmpty(weight) ||
      isEmpty(gender) ||
      isEmpty(goal.toString())
    ) {
      setState((prev) => {
        return { ...prev, errorMessage: t('Fill all the fields') };
      });
      return;
    }

    if (!validation.validatePhoneNumber(username)) {
      setState((prev) => {
        return { ...prev, errorMessage: t('enterValidPhoneNumber') };
      });
      return;
    }

    setState((prev) => {
      return { ...prev, errorMessage: '' };
    });

    updateUserInfoHandler.mutate({
      userId: user?._id || user?.id,
      body: {
        dob: birthdate,
        imageUrl,
        phoneNo: username,
        firstName,
        lastName,
        weight,
        height,
        gender,
        goal,
      },
    });
  };

  const handleSetImage = async (e) => {
    try {
      const image = e.target.files[0];

      const response = await axios.get(
        `${process.env.REACT_APP_EXPRESS_URL}/api/users/${user?._id}/getSignedImageURL`,
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: localStorage.getItem('auth'),
            'ngrok-skip-browser-warning': 'true',
          },
        },
      );

      const postURL = response?.data?.data?.postURL;

      await axios.put(postURL, image, {
        headers: {
          'Content-Type': 'image/jpeg',
        },
      });

      setState((prev) => {
        return { ...prev, imageUrl: postURL.split('?')[0], imageToDisplay: URL.createObjectURL(image) };
      });
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const heightOptions = () => {
    const list = [];
    map([3, 4, 5, 6], (feet) => {
      return range(0, 12).map((value) => list.push(`${feet}' ${value}"`));
    });

    return list;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 4,
        bgcolor: '#fff',
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2,
        }}
      >
        <Typography variant="h4" color="rgba(29, 29, 31, 1)">
          {t('generalInfo')}
        </Typography>
        <Button text={t('saveChanges')} onClick={handleSubmit} loading={updateUserInfoHandler.isLoading} />
      </Box>

      <Grid
        container
        spacing={isMobile ? 2 : 6}
        flex={isMobile ? 'column' : 'undefined'}
        justifyContent={isMobile ? 'center' : 'space-between'}
      >
        <Grid item>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Avatar src={state.imageToDisplay} sx={{ width: 180, height: 180 }} />
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                width: 180,
              }}
            >
              <input type="file" ref={imageRef} style={{ display: 'none' }} onChange={handleSetImage} />
              <Button
                style={{ fontSize: '10px' }}
                bg="light"
                onClick={() => imageRef.current.click()}
                text={t('changePhoto')}
              />

              <IconButton
                type={ICON_BUTTON_TYPE.delete}
                onClick={() =>
                  setState((prev) => {
                    return { ...prev, imageUrl: '', imageToDisplay: '' };
                  })
                }
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs>
          <Grid
            container
            spacing={1}
            sx={{ display: isMobile ? 'flex' : 'undefined', flexDirection: isMobile ? 'column' : 'row' }}
          >
            <Grid item xs={isMobile ? 12 : 6}>
              <TextField
                label={t('firstName')}
                value={state.firstName}
                fullWidth
                InputProps={{
                  sx: { borderRadius: '50px' },
                }}
                onChange={(e) =>
                  setState((prev) => {
                    return {
                      ...prev,
                      firstName: e.target.value,
                    };
                  })
                }
              />
            </Grid>
            <Grid item xs={isMobile ? 12 : 6}>
              <TextField
                label={t('lastName')}
                value={state.lastName}
                fullWidth
                InputProps={{
                  sx: { borderRadius: '50px' },
                }}
                onChange={(e) =>
                  setState((prev) => {
                    return {
                      ...prev,
                      lastName: e.target.value,
                    };
                  })
                }
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4 }}>
            <TextField
              label={t('email')}
              value={state.email}
              fullWidth
              InputProps={{
                sx: { borderRadius: '50px' },
              }}
              disabled={true}
            />
          </Box>

          <Grid container spacing={1} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <TextField
                label={t('goal')}
                value={state.goal}
                fullWidth
                InputProps={{
                  sx: { borderRadius: '50px' },
                }}
                type="number"
                onChange={(event) => {
                  setState((prev) => ({
                    ...prev,
                    goal: event.target.value,
                  }));
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <SelectInput
                fullWidth
                label={t('gender')}
                value={state.gender}
                onChange={(event) => {
                  setState((prev) => ({
                    ...prev,
                    gender: event.target.value,
                  }));
                }}
                options={['male', 'female', 'other']}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ mt: 2 }}>
            <Grid item xs={isMobile ? 12 : 6}>
              <PhoneInput
                placeholder={t('enterPhoneNumber')}
                defaultCountry={country}
                value={state.username}
                numberInputProps={{
                  className: 'input-child input-child-2',
                }}
                onChange={(value) =>
                  setState((prev) => {
                    return {
                      ...prev,
                      username: value,
                    };
                  })
                }
                disabled={true}
              />
            </Grid>
            <Grid item xs={isMobile ? 12 : 6}>
              <TextField
                label={t('birthday')}
                value={state.birthdate}
                fullWidth
                type="date"
                InputProps={{
                  sx: { borderRadius: '50px' },
                }}
                onChange={(e) =>
                  setState((prev) => {
                    return {
                      ...prev,
                      birthdate: e.target.value,
                    };
                  })
                }
              />
            </Grid>
          </Grid>
          <Grid container direction={'row'} marginTop="30px">
            <Grid sm={11} md={6} lg={6} xs={11}>
              <Box sx={{ width: '98%' }}>
                <SelectInput
                  fullWidth
                  label={t('weight')}
                  options={range(50, 500).map((value) => `${value} lb`)}
                  value={state.weight}
                  onChange={(e) => setState((prev) => ({ ...prev, weight: e.target.value }))}
                />
              </Box>
            </Grid>
            <Grid sm={11} md={6} lg={6} xs={11}>
              <Box sx={{ width: '98%', marginLeft: '5px' }}>
                <SelectInput
                  fullWidth
                  label={t('height')}
                  value={state.height}
                  onChange={(event) => {
                    const value = event.target.value;
                    return setState((prevState) => {
                      return { ...prevState, height: value };
                    });
                  }}
                  options={heightOptions()}
                />
              </Box>
            </Grid>
          </Grid>
          <Typography variant="subtitle1" style={{ color: 'red', marginTop: '30px' }} textAlign="center">
            {state.errorMessage}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PersonalInfo;
